import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropzone } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';
import type { CsfDataViewDto } from '@subscriptions/application/dtos';
import { isServerError } from '@subscriptions/presentation/utils';
import {
  FISCAL_PROOF_EXTENSION,
  FISCAL_PROOF_MAX_SIZE_BYTES,
  FISCAL_PROOF_MIME_TYPE,
} from '@subscriptions/presentation/constants';
import { useExtractCsfData } from '@subscriptions/presentation/hooks';
import { CsfFileCard } from './CsfFileCard';

interface CsfUploadSectionProps {
  className?: string;
  disabled?: boolean;
  /** Controlled file value. When provided, the component operates in controlled mode. */
  file?: File | null;
  onDirtyChange?: (isDirty: boolean) => void;
  onExtracted: (csfData: CsfDataViewDto) => void;
  /** Called when the file changes (upload or removal). Required for controlled mode. */
  onFileChange?: (file: File | null) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

/**
 * Compact CSF upload section that shows a dropzone or uploaded file state.
 *
 * Designed to be embedded in the invoice data form, not as a standalone step.
 * When a valid PDF is selected, extraction runs automatically and `onExtracted`
 * is called with the parsed data. The file can be removed to upload a different one.
 *
 * Supports both controlled mode (pass `file` and `onFileChange`) and uncontrolled mode
 * (internal state). Use controlled mode when the file state needs to persist across
 * component remounts (e.g., when toggling between upload-only and form views).
 *
 * @param {CsfUploadSectionProps} props - Component properties
 * @returns {React.ReactElement} CSF upload section
 */
export const CsfUploadSection = ({
  className,
  disabled = false,
  file: controlledFile,
  onDirtyChange,
  onExtracted,
  onFileChange,
  onLoadingChange,
}: CsfUploadSectionProps): React.ReactElement => {
  const { t } = useTranslation('invoiceData');
  const [internalFile, setInternalFile] = useState<File | null>(null);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledFile !== undefined;
  const file = isControlled ? controlledFile : internalFile;

  const { data: csfData, error: queryError, isLoading: isExtracting } = useExtractCsfData(file);
  const onExtractedRef = useRef(onExtracted);
  onExtractedRef.current = onExtracted;

  useEffect(() => {
    onDirtyChange?.(Boolean(file));
  }, [file, onDirtyChange]);

  useEffect(() => {
    onLoadingChange?.(isExtracting);
  }, [isExtracting, onLoadingChange]);

  // React to successful extraction
  useEffect(() => {
    if (csfData) {
      onExtractedRef.current(csfData);
    }
  }, [csfData]);

  const extractionError = queryError
    ? t(`invoiceDataForm.uploadStep.${isServerError(queryError) ? 'serverError' : 'extractError'}`)
    : null;

  const handleFileChange = (value: File | File[] | null) => {
    const selectedFile = Array.isArray(value) ? value[0] : value;
    const nextFile = selectedFile ?? null;

    if (isControlled) {
      onFileChange?.(nextFile);
    } else {
      setInternalFile(nextFile);
    }
  };

  const handleRemoveFile = () => {
    if (isControlled) {
      onFileChange?.(null);
    } else {
      setInternalFile(null);
    }
  };

  return (
    <div className={cn('flex flex-col gap-2 font-sans', className)}>
      {!file && (
        <Dropzone
          accept={{ [FISCAL_PROOF_MIME_TYPE]: [FISCAL_PROOF_EXTENSION] }}
          description={t('invoiceDataForm.uploadStep.dropHint')}
          disabled={disabled || isExtracting}
          fileTypeHint={t('invoiceDataForm.uploadStep.hint')}
          maxSize={FISCAL_PROOF_MAX_SIZE_BYTES}
          multiple={false}
          onFileChange={handleFileChange}
          title={t('invoiceDataForm.uploadStep.label')}
        />
      )}

      {file && (
        <CsfFileCard
          error={extractionError}
          file={file}
          isExtracting={isExtracting}
          onRemove={disabled ? undefined : handleRemoveFile}
        />
      )}
    </div>
  );
};
